import './Subscription.scss'

import { Col, Container, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useEffect, useContext, useState } from 'react'
import { Link } from 'react-router-dom'

import { getCurrentSubscription } from 'components/common/api/Payments'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import AccountMenu from 'components/common/navigation/AccountMenu'
import Membership from '../Membership'
import {
  getAnnualSubscriptionCost,
  getDaysInPeriod,
  getDaysRemaining,
  getReadableSubscriptionPeriod,
  SUBSCRIPTION_PERIOD_ANNUAL
} from 'components/common/api/Payments'
import { navigateTo } from 'components/common/navigation'
import { ROUTE_SUBSCRIPTION_CANCEL } from 'components/common/helpers/Paths'
import { useHistory } from 'react-router-dom'
import { isiOS } from 'components/common/helpers/Mobile'

const daysRemainingPercentage = (subscription, dateField) => {
  const daysRemaining = getDaysRemaining(subscription, dateField)
  const daysInPeriod = getDaysInPeriod(subscription.plan_period)

  return Math.round((daysRemaining / daysInPeriod) * 100)
}

/**
 * Simple Subscription component. Used to display the user's subscription details
 */
export default function Subscription({ setHeaderTitle, setReturnPath }) {
  const { setUserSubscription } = useContext(SubscriptionContext)
  const [subscription, setSubscription] = useState()
  const history = useHistory()

  /**
   * Simple helper function to see if the user has a subscription
   * @returns A boolean indicating whether or not the user has a subscription
   */
  const hasSubscription = () =>
    subscription &&
    Object.entries(subscription).length > 0 &&
    subscription.active &&
    Number(subscription.plan_id) !== 0

  // If the user is using iOS and on version 1.3.0 or higher, we can use the
  // native in-app purchase flow.
  // IMPORTANT NOTE: Some users may have an older version of the app installed
  // and/or may have an existing subscription. So we need to check for both and
  // allow them to cancel their subscription.
  const isInAppPurchase = isiOS('1.3.0')

  /**
   * Event handler for clicking "cancel subscription"
   * @param {Event} event The click event that triggered the handler
   */
  const handleCancelSubscription = event => {
    event.preventDefault()
    event.stopPropagation()
    navigateTo(ROUTE_SUBSCRIPTION_CANCEL, history)
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Update header and return path
    setHeaderTitle('Subscription')
    setReturnPath(null)

    // Return early if we can't update the user's subscription
    if (!setUserSubscription) return

    // Update the user's subscription context
    async function getSubscriptionAsync() {
      const sub = await getCurrentSubscription()
      setUserSubscription(sub)
      setSubscription(sub)
    }
    getSubscriptionAsync()
  }, [setHeaderTitle, setReturnPath, setUserSubscription])

  return (
    <Container className='subscription-container'>
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8} className={'mt-4'}>
          {/* Does the user have a subscription? */}
          {!hasSubscription() ? (
            <Membership />
          ) : (
            <>
              <Row>
                <Col>
                  <div className='countdown'>
                    <svg viewBox='0 0 36 36' className='countdown-circle'>
                      <path
                        className='circle-bg'
                        d='M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831'
                      />
                      <path
                        className='circle'
                        strokeDasharray={`${daysRemainingPercentage(
                          subscription,
                          subscription.ends_on ? 'ends_on' : 'renews_on'
                        )}, 100`}
                        d='M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831'
                      />
                    </svg>

                    {/* If we have an end date, show the end date  */}
                    <span className='countdown-text'>
                      {subscription.ends_on ? (
                        <>
                          ends in
                          <strong>{getDaysRemaining(subscription)} days</strong>
                        </>
                      ) : (
                        <>
                          {/* Check to see if our start date is in the future */}
                          {getDaysRemaining(subscription, 'starts_on') > 0 ? (
                            <>
                              Trial ends in
                              <strong>
                                {getDaysRemaining(subscription, 'starts_on')}{' '}
                                days
                              </strong>
                            </>
                          ) : (
                            <>
                              {/* Check to see if our renewal date is greater than 0 or a valid number */}
                              {getDaysRemaining(subscription, 'renews_on') >
                                0 && (
                                <>
                                  renews in
                                  <strong>
                                    {getDaysRemaining(
                                      subscription,
                                      'renews_on'
                                    )}{' '}
                                    days
                                  </strong>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row className='text-center mt-3'>
                <Col>
                  {/* Does the first payment differ from the rest? */}
                  {subscription.first_payment_cost !==
                    subscription.subscription_cost && (
                    <>
                      <h5 className='subscription-amount'>
                        ${subscription.first_payment_cost.toFixed(2)} First
                        Payment
                      </h5>
                      <p>then</p>
                    </>
                  )}
                  <h5>
                    ${subscription.subscription_cost.toFixed(2)}{' '}
                    {getReadableSubscriptionPeriod(subscription)}
                  </h5>
                  {/* Don't show yearly price if it's a yearly subscription */}
                  {subscription.plan_period !== SUBSCRIPTION_PERIOD_ANNUAL && (
                    <h6>
                      ${getAnnualSubscriptionCost(subscription).toFixed(2)} Year
                    </h6>
                  )}
                  {subscription.signup_code && (
                    <p className='green'>
                      <strong>Signup Code:</strong>{' '}
                      <span>{subscription.signup_code}</span>
                    </p>
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group
                    controlId='formRenewal'
                    className='form-group visible'
                  >
                    <FloatingLabel controlId='floatingRenewal' label='Renews'>
                      <Form.Control
                        name='renewal'
                        value={getReadableSubscriptionPeriod(subscription)}
                        size='lg'
                        placeholder='Renews'
                        disabled={true}
                        type='text'
                      />
                    </FloatingLabel>
                  </Form.Group>

                  {/* Don't show start date if it ends on the same date as the end date */}
                  {subscription.ends_on !== subscription.starts_on && (
                    <Form.Group
                      controlId='formRenewal'
                      className='form-group visible'
                    >
                      <FloatingLabel controlId='floatingRenewal' label='Starts'>
                        <Form.Control
                          name='renewal'
                          value={subscription.starts_on}
                          size='lg'
                          placeholder='Starts'
                          disabled={true}
                          type='text'
                        />
                      </FloatingLabel>
                    </Form.Group>
                  )}

                  <Form.Group
                    controlId='formRenewal'
                    className='form-group visible'
                  >
                    <FloatingLabel controlId='floatingRenewal' label='Ends'>
                      <Form.Control
                        name='renewal'
                        value={
                          subscription.ends_on ? subscription.ends_on : 'Never'
                        }
                        size='lg'
                        placeholder='Ends'
                        disabled={true}
                        type='text'
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>

              {/* Don't show cancellation option if we already have an end date 
                  or if the user purchased with an In-app Purchse */}
              {!isInAppPurchase && !subscription.ends_on && (
                <Row className='mt-4 text-right'>
                  <Col>
                    <Link to={'#'} onClick={handleCancelSubscription}>
                      Cancel subscription
                    </Link>
                  </Col>
                </Row>
              )}

              {/* If the device supports in-app purchases, let's notify the user */}
              {isInAppPurchase && (
                <Row className='mt-4'>
                  <Col>
                    <p className='muted'>
                      You can manage your subscription in the{' '}
                      <strong>App Store</strong> or in your device{' '}
                      <strong>Settings</strong>.
                    </p>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  )
}
